<template>
  <div class="content">
      <div
        v-if="statusMessage && !statusDismissed"
        class="alert alert-dismissible fade show"
        :class="statusType === 'success' ? 'alert-success' : 'alert-danger'"
        role="alert"
      >
        {{ statusMessage }}
        <button
          type="button"
          class="btn-close"
          aria-label="Close"
          @click="dismissStatus"
        ></button>
      </div>
      <!-- Tabela z listą użytkowników (tylko jeśli nie jesteśmy w trybie edycji) -->
      <table class="role-table table table-striped align-middle" v-if="!edit">
        <thead class="thead-light">
            <tr>
            <!-- Nagłówki kolumn -->
            <th style="width: 5%">{{ t('ordinal-number') }}</th>
            <th style="width: 15%">{{ t('name2') }}</th>
            <th style="width: 70%">{{ t('description') }}</th>
            <th style="width: 5%">{{ t('action') }}</th>
            <th style="width: 5%"></th>
            </tr>
        </thead>
        <tbody>
            <!-- Pętla po użytkownikach wyświetlanych na danej stronie -->
            <tr v-for="roles in paginatedRoles" :key="roles.id" class="roles-list">
            <td>{{ roles.id }}</td>
            <td>{{ roles.name }}</td>
            <td>
                <!-- Zwraca uprawnienia użytkownika (roles jako tekst) -->
                {{ roles.description }}
            </td>
            <td>
                <!-- Przycisk do przejścia w tryb edycji wybranego użytkownika -->
                <button
                type="button"
                @click="setToEdit(roles.id)"
                class="btn button-edit"
                >
                {{ t('edit') }}
                </button>
            </td>
            <td>
                <!-- Checkbox do zaznaczania użytkownika do usunięcia -->
                <input-checkbox
                @click="setToDelete(roles.id)"
                :checked-condition="toDelete.includes(roles.id)"
                ></input-checkbox>
            </td>
            </tr>
        </tbody>
      </table>
      <!-- Panel paginacji i przycisk usuwania zaznaczonych użytkowników (gdy nie w trybie edycji) -->
      <div class="actions" v-if="!edit">
      <PagePanel
          :page-num="pageNumber"
          :page-size="pageSize"
          :total-size="totalCount"
          v-on:setPage="setPage"
      />
      <button
          type="button"
          @click="deleteRoles"
          :disabled="toDelete.length === 0"
          class="remove btn button-delete"
      >
          {{ t('delete') }}
      </button>
      </div>
      <!-- Komponent formularza edycji użytkownika (po kliknięciu przycisku edytuj) -->
      <div class="content" v-if="edit">
        <EditRole :role-id="toEdit" v-on:goBack="handleGoBack" />
      </div>
  </div>
</template>

<script>
// Importujemy narzędzia i komponenty
import moment from "moment"; // Do formatowania dat
import RolesService from "../services/Roles"; // Serwis pobierania użytkowników z backendu
import InputCheckbox from "@/components/InputCheckbox"; // Własny komponent checkboxa
import EditRole from "@/components/EditRole"; // Komponent do edycji użytkownika
import PagePanel from "@/components/table/PagePanel"; // Komponent paginacji

moment.locale("pl"); // Ustawienie polskiego locale

export default {
  components: { PagePanel, EditRole, InputCheckbox }, // Deklarujemy używane komponenty
  name: "RoleList",
  data() {
    return {
      loading: false,         // Czy trwa ładowanie użytkowników
      roles: [],              // Lista użytkowników z backendu (wszystkich)
      filteredRoles: [],      // Lista użytkowników po filtracji (np. po wyszukiwaniu)
      pageNumber: 1,          // Numer bieżącej strony (dla paginacji)
      pageSize: 10,           // Liczba użytkowników na stronie
      totalCount: 10,         // Łączna liczba użytkowników
      error: null,            // Pole na błąd
      moment: moment,         // Instancja moment.js
      dataService: RolesService,     // Serwis komunikacji z backendem
      toDelete: [],           // Tablica ID użytkowników zaznaczonych do usunięcia
      edit: false,            // Czy jesteśmy w trybie edycji użytkownika
      toEdit: null,           // ID użytkownika aktualnie edytowanego
      statusMessage: "",
      statusType: "error",
      statusDismissed: false,
    };
  },
  props: {
    searchQuery: {
      type: String,
      default: '',
    },
  },
  watch: {
    // Reaguje na zmianę zapytania wyszukiwania
    searchQuery(newQuery) {
      this.filterRoles(newQuery);
    },
  },
  created() {
    // Automatycznie pobierz listę użytkowników po załadowaniu komponentu
    this.retrieveRoles();
  },
  computed: {
    // Zwraca użytkowników wyświetlanych na bieżącej stronie (paginacja)
    paginatedRoles() {
      const startIndex = (this.pageNumber - 1) * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      return this.filteredRoles.slice(startIndex, endIndex);
    },
  },
  methods: {
    // Filtrowanie użytkowników po nazwie użytkownika (case-insensitive)
    filterRoles(query) {
      if (!query) {
        this.filteredRoles = this.roles;
      } else {
        const lowerCaseQuery = query.toLowerCase();
        this.filteredRoles = this.roles.filter((role) =>
          role.name.toLowerCase().includes(lowerCaseQuery)
        );
      }
      this.pageNumber = 1; // Po filtracji wracamy do pierwszej strony
      this.$forceUpdate(); // Wymuszamy odświeżenie komponentu
    },
    // Powrót z widoku edycji użytkownika do listy
    handleGoBack() {
      this.edit = false;
      this.toEdit = null;
      this.$emit('editMode', false);
      this.retrieveRoles(); // Po powrocie odśwież listę użytkowników
    },
    // Ustaw numer strony (z paginacji)
    setPage(event) {
      this.pageNumber = event;
      this.refreshData();
    },
    // Pobierz użytkowników z backendu (przy ładowaniu komponentu lub po powrocie z edycji)
    retrieveRoles() {
      this.loading = true;
      this.toDelete = [];
      this.toEdit = null;
      this.edit = false;
      this.refreshData();
    },
    // Pobierz i uaktualnij listę użytkowników (np. po zmianie strony)
    refreshData() {
      this.loading = true;
      this.error = null;
      this.roles = [];

      this.dataService
        .getRoleList(this.pageNumber, this.pageSize)
        .then((res) => {
          // res może być tablicą albo obiektem { content, totalCount }
          const list = Array.isArray(res)
            ? res
            : (Array.isArray(res?.content) ? res.content : []);

          this.roles = list;
          // zachowaj filtr po searchQuery jeśli jest ustawiony
          if (this.searchQuery) {
            const q = this.searchQuery.toLowerCase();
            this.filteredRoles = list.filter(r => r.name?.toLowerCase().includes(q));
          } else {
            this.filteredRoles = list;
          }

          this.totalCount = Array.isArray(res)
            ? list.length
            : (Number(res?.totalCount) || list.length);
        })
        .catch((err) => {
          console.error(err);
          this.error = err;
          this.showStatus("Nie udało się pobrać listy ról.", "error");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // Przejdź do widoku edycji użytkownika (po kliknięciu "Edytuj")
    setToEdit(id) {
      this.toDelete = [];
      this.edit = true;
      this.toEdit = id;
      this.$emit('editMode', true);
    },
    // Zaznacz lub odznacz użytkownika do usunięcia (checkbox)
    setToDelete(id) {
      if (this.toDelete.includes(id)) {
        this.toDelete.splice(this.toDelete.indexOf(id), 1);
      } else {
        this.toDelete.push(id);
      }
    },
    // Usuń wszystkich zaznaczonych użytkowników (po kliknięciu "Usuń")
    deleteRoles() {
      RolesService.deleteRoles(this.toDelete)
        .then(() => {
          this.toDelete = [];
          this.$emit("clearSearch"); // Wyczyść zapytanie wyszukiwania (jeśli było)
          this.refreshData();
          this.showStatus("Role zostały usunięte.", "success");
        })
        .catch((error) => {
          this.loading = false;
          console.log(error);
          this.showStatus("Nie udało się usunąć ról.", "error");
        });
    },
    showStatus(message, type = "error") {
      this.statusMessage = message;
      this.statusType = type;
      this.statusDismissed = false;
    },
    dismissStatus() {
      this.statusDismissed = true;
    },
  },
};
</script>

<style scoped>
.content {
  width: 100%;
  min-height: 270px;
  margin-bottom: 20px;
}
.content > .alert {
  margin: 1rem;
}
.content > .alert-success {
  color: #155724;
  background-color: #d4edda;
  border-color: #c3e6cb;
}

h3 {
  padding: 3px 15px;
  background-color: white;
}

.actions {
  position: relative;
}

.pager {
  margin-bottom: 1rem;
  padding-bottom: 1.75rem;
  padding-top: 0.5rem;
}

.remove {
  position: absolute;
  right: 36px;
  top: 0;
}

.role-table {
  width: 100%;
  font-size: 12px;
  color: black;
}

.role-table th,
.role-table td {
  vertical-align: middle !important;
}

/* Twój dotychczasowy padding podbijał komórki do góry – usuń / nadpisz */
.roles-list td {
  padding-top: 0;                 /* usuń stary */
  padding: .75rem .5rem;          /* standardowy padding komórki */
}

.roles-list td:nth-child(5) {
  padding-top: 0.5rem;
}

/* .role-list td:nth-child(4),
.role-list td:nth-child(5) {
  text-align: center;
} */

.button-edit {
  padding: 0.5rem 1rem 0.5rem 1rem;
} 

.button-edit:hover {
  background-color: #C77501 !important;
  color: white !important;
}

.button-delete {
  padding: 0.5rem 1.20rem 0.5rem 1.20rem;
  margin-top: 0.25rem;
  margin-right: 1.45rem;
}

.button-delete:hover {
  background-color: #e14544 !important;
  color: white !important;
}
</style>
